import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    const employee = localStorage.getItem("employee");
    const needsPwChange = localStorage.getItem("must_change_password");
    if (token && employee) {
      try {
        setUser(JSON.parse(employee));
        if (needsPwChange === "true") setMustChangePassword(true);
      } catch {
        localStorage.clear();
      }
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    const { access_token, refresh_token, employee, must_change_password } = response.data;
    localStorage.setItem("access_token", access_token);
    localStorage.setItem("refresh_token", refresh_token);
    localStorage.setItem("employee", JSON.stringify(employee));
    if (must_change_password) {
      localStorage.setItem("must_change_password", "true");
      setMustChangePassword(true);
    }
    setUser(employee);
    return { employee, must_change_password };
  };

  const completePasswordChange = () => {
    localStorage.removeItem("must_change_password");
    setMustChangePassword(false);
  };

  const logout = useCallback(() => {
    localStorage.clear();
    setUser(null);
    setMustChangePassword(false);
  }, []);

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  }), []);

  // Gleichzeitig abgelehnte Anfragen teilen sich einen Erneuerungsversuch
  const laufendeErneuerung = useRef(null);

  const erneuern = useCallback(async () => {
    try {
      const refresh = localStorage.getItem("refresh_token");
      if (!refresh) throw new Error("No refresh token");
      const response = await axios.post(`${API}/auth/refresh`, { refresh_token: refresh });
      const { access_token, refresh_token: newRefresh, employee } = response.data;
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", newRefresh);
      localStorage.setItem("employee", JSON.stringify(employee));
      setUser(employee);
      return access_token;
    } catch {
      logout();
      throw new Error("Session expired");
    }
  }, [logout]);

  const refreshToken = useCallback(() => {
    if (!laufendeErneuerung.current) {
      laufendeErneuerung.current = erneuern().finally(() => {
        laufendeErneuerung.current = null;
      });
    }
    return laufendeErneuerung.current;
  }, [erneuern]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 403 && error.response?.data?.detail === "Bitte zuerst das Passwort ändern.") {
          localStorage.setItem("must_change_password", "true");
          setMustChangePassword(true);
          return Promise.reject(error);
        }
        // 401 von Anmeldung/Erneuerung selbst nie erneut erneuern, sonst Endlosschleife
        const url = error.config?.url || "";
        const istAnmeldeAnfrage = url.includes("/auth/login") || url.includes("/auth/refresh");
        if (error.response?.status === 401 && !error.config._retry && !istAnmeldeAnfrage) {
          error.config._retry = true;
          try {
            const newToken = await refreshToken();
            error.config.headers["Authorization"] = `Bearer ${newToken}`;
            return axios(error.config);
          } catch {
            logout();
            return Promise.reject(error);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [refreshToken, logout]);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, getAuthHeaders, mustChangePassword, completePasswordChange }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
